// ─── ThermalXpert API Client ──────────────────────────────────────────────────
// All API calls live here. WP plugin endpoint (wp-json/thermalxpert/v1/*) is
// the single path for form submissions — HouseCall Pro is a server-side API
// that cannot be called from the browser due to CORS (by HCP's design).
// Content fetching tries WP, falls back to FALLBACK_CONTENT from fallback-content.ts

import { FALLBACK_CONTENT, type SiteContent } from "./fallback-content";

// ─── Config ───────────────────────────────────────────────────────────────────
const WP_API   = (import.meta.env.VITE_WP_API_URL  || "https://thermalxpert.nyc/wp-json").replace(/\/$/, "").replace(/^http:/, "https:");
const TIMEOUT  = 6000; // ms — if WP doesn't respond in 6s, surface error

// ─── Helpers ──────────────────────────────────────────────────────────────────
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), ms)
    ),
  ]);
}

// ─── Content API ──────────────────────────────────────────────────────────────

// Derive site base URL from WP_API (strip /wp-json)
const WP_BASE = WP_API.replace(/\/wp-json$/, "");

async function tryFetch(url: string): Promise<Response> {
  return withTimeout(
    fetch(url, { cache: "no-store", redirect: "follow", headers: { Accept: "application/json" } }),
    TIMEOUT
  );
}

export async function fetchSiteContent(): Promise<SiteContent> {
  const attempts = [
    // 1. Pretty permalink style: /wp-json/thermalxpert/v1/content
    `${WP_API}/thermalxpert/v1/content`,
    // 2. Plain permalink fallback: /?rest_route=/thermalxpert/v1/content
    `${WP_BASE}/?rest_route=/thermalxpert/v1/content`,
  ];

  for (const url of attempts) {
    try {
      const res = await tryFetch(url);
      if (!res.ok) continue;
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("json")) continue; // got HTML (WP theme) — skip
      const data = await res.json();
      console.info(`[ThermalXpert] Content loaded from: ${url}`);
      return deepMerge(FALLBACK_CONTENT, data) as SiteContent;
    } catch {
      // try next
    }
  }

  console.warn("[ThermalXpert] WP content API unreachable — using local fallback.");
  return FALLBACK_CONTENT;
}

// ─── Internal WP POST helper ──────────────────────────────────────────────────

async function wpPost(
  path: string,
  payload: Record<string, unknown>
): Promise<{ success: boolean; message: string }> {
  const urls = [
    `${WP_API}/thermalxpert/v1/${path}`,
    `${WP_BASE}/?rest_route=/thermalxpert/v1/${path}`,
  ];

  for (const url of urls) {
    try {
      const res = await withTimeout(
        fetch(url, {
          method:  "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body:    JSON.stringify(payload),
          redirect: "follow",
        }),
        TIMEOUT
      );
      if (!res.ok) continue;
      const ct = res.headers.get("content-type") || "";
      if (!ct.includes("json")) continue;
      const data = await res.json();
      return { success: true, message: data.message || "Received!" };
    } catch {
      // try next URL
    }
  }

  // Both WP URLs failed — surface a clear message; no browser→HCP fallback
  // (HouseCall Pro's API blocks cross-origin requests by design)
  console.error(`[ThermalXpert] WP endpoint /thermalxpert/v1/${path} unreachable.`);
  return {
    success: false,
    message: "Our server is temporarily unavailable. Please call us directly at (929) 678-1640 or email support@thermalxpert.nyc.",
  };
}

// ─── Contact form ─────────────────────────────────────────────────────────────

export interface ContactPayload {
  name:    string;
  email:   string;
  phone:   string;
  address: string;
  message: string;
}

export async function submitContact(payload: ContactPayload): Promise<{ success: boolean; message: string }> {
  return wpPost("contact", payload as unknown as Record<string, unknown>);
}

// ─── Customer registration ─────────────────────────────────────────────────────

export interface RegisterPayload {
  first_name:    string;
  last_name:     string;
  email:         string;
  mobile_number: string;
  home_number:   string;
  company:       string;
  street:        string;
  city:          string;
  state:         string;
  zip:           string;
  notes:         string;
}

export async function submitRegistration(payload: RegisterPayload): Promise<{ success: boolean; message: string }> {
  return wpPost("register", payload as unknown as Record<string, unknown>);
}

// ─── Deep merge utility ───────────────────────────────────────────────────────
// WP data is merged ON TOP of fallback, so any missing WP keys keep fallback values.
function deepMerge(target: Record<string, unknown>, source: Record<string, unknown>): Record<string, unknown> {
  const result = { ...target };
  for (const key of Object.keys(source)) {
    if (
      source[key] !== null &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key]) &&
      typeof target[key] === "object" &&
      !Array.isArray(target[key])
    ) {
      result[key] = deepMerge(
        target[key] as Record<string, unknown>,
        source[key] as Record<string, unknown>
      );
    } else if (source[key] !== undefined && source[key] !== null) {
      result[key] = source[key];
    }
  }
  return result;
}
